import { useEffect, useMemo } from "react";

import { usePresignURL } from "@app/hooks/s3";
import { useImageReferences as useFireflyImageReferences } from "@app/hooks/firefly";
import { useUpdateAssets } from "@app/hooks/videoWizard";

/**
 * Presigns reference images and uploads them to Firefly, so a generation can
 * point at them by upload id.
 *
 * Every distinct file path of `assets` is presigned in one batched request —
 * the resulting `urlByPath` is what the labeling hook and the reference
 * thumbnails read — and each asset is uploaded once, keyed by its file path so
 * the upload is redone when the asset changes.
 *
 * When the upload fails there is nothing to generate from, so the `fallbacks`
 * are written instead: each entry's asset is filed under the variation key it
 * stands in for, which is what keeps the wizard moving with the reference
 * image itself rather than a missing one.
 *
 * Usable on its own: pass the reference assets, and optionally the fallbacks
 * to apply when their upload fails.
 *
 * @param {object} options
 * @param {string} options.projectId - The video/project the assets belong to.
 * @param {Array<object>} options.assets - Resolved reference assets, memoized.
 * @param {Array<{key: string, asset: object}>} [options.fallbacks] - Default
 *   assets to write when the upload fails, memoized.
 * @param {Set<string>} [options.uploadKeys] - Names of the assets to upload,
 *   memoized. Every asset is still presigned, but only these are uploaded;
 *   all of them when omitted.
 * @param {boolean} [options.enabled] - Whether the upload should run.
 */
export const useImageReferenceUploads = ({
  projectId,
  assets,
  fallbacks,
  uploadKeys,
  enabled = true,
}) => {
  const updateAssets = useUpdateAssets(projectId);
  const { mutate: mutateUpdateAssets } = updateAssets;

  // One batched presign for every reference image — an image referenced by
  // several variations is presigned once.
  const filePaths = useMemo(
    () =>
      Array.from(
        new Set((assets ?? []).map((asset) => asset?.filePath).filter(Boolean)),
      ),
    [assets],
  );

  const presign = usePresignURL(filePaths, {
    // An empty array is still truthy — do not presign "nothing".
    enabled: filePaths.length > 0,
  });

  const urlByPath = useMemo(
    () =>
      new Map(
        (presign.data?.urls ?? []).map((entry) => [
          entry?.filePath,
          entry?.presignURL,
        ]),
      ),
    [presign.data?.urls],
  );

  const references = useFireflyImageReferences(
    useMemo(
      () =>
        (assets ?? []).map((asset) => ({
          key: asset?.name,
          // Upload directly by S3 filePath (or presigned URL if already cached)
          // so reference uploads do not wait for the browser presign round-trip.
          url:
            !uploadKeys || uploadKeys.has(asset?.name)
              ? (urlByPath.get(asset?.filePath) ?? asset?.filePath)
              : undefined,
          // Keyed by file path so the upload is re-done when the asset changes
          cacheKey: asset?.filePath,
        })),
      [assets, urlByPath, uploadKeys],
    ),
    { enabled },
  );

  // Set default images for the variations if the image reference upload fails
  useEffect(() => {
    if (!references.isError || updateAssets.isPending || !fallbacks?.length) {
      return;
    }

    const missing = fallbacks.filter((fallback) => !!fallback?.asset?.filePath);

    if (!missing.length) {
      return;
    }

    console.warn("Error uploading image reference:", references.error);
    console.info("Setting default images for all variations.");

    mutateUpdateAssets({
      assets: missing.map(({ key, asset }) => ({ ...asset, name: key })),
    });
  }, [
    references.isError,
    references.error,
    fallbacks,
    updateAssets.isPending,
    mutateUpdateAssets,
  ]);

  return useMemo(
    () => ({
      byKey: references.byKey,
      urlByPath,
      isFetching: references.isFetching,
      isError: references.isError,
      error: references.error,
      isPresigning: presign.isFetching,
    }),
    [
      references.byKey,
      references.isFetching,
      references.isError,
      references.error,
      urlByPath,
      presign.isFetching,
    ],
  );
};
